import { useTranslation } from 'react-i18next';

import type { SearchSessionsPage } from '@entities/search-session';
import { ProgressBar } from '@shared/ui';

import styles from './SearchesPage.module.css';

interface OrdersSummaryProps {
  page: SearchSessionsPage;
}

/**
 * Сводка и лимит заказов в правом сайдбаре `/app` (DESIGN_SYSTEM §7, FE-200).
 * Карточка рельса с заголовком — та же, что у прогресса анализа (§5.2).
 *
 * **Все числа — из ответа API** (DESIGN_SYSTEM §8): сколько заказов активно,
 * сколько в архиве, сколько показывает текущий фильтр и каков лимит. Ничего не
 * складывается «по видимой странице»: счётчик, который меняется при переходе
 * на вторую страницу, — это не сводка, а недоразумение.
 */
export function OrdersSummary({ page }: OrdersSummaryProps) {
  const { t } = useTranslation();
  const ratio = page.limit > 0 ? Math.min(page.active_count / page.limit, 1) : 0;

  return (
    <section className={styles.summary} aria-labelledby="orders-summary-title">
      <h2 id="orders-summary-title" className={styles.summaryTitle}>
        {t('searches.summary.label')}
      </h2>
      <dl className={styles.summaryList}>
        <div className={styles.summaryRow}>
          <dt>{t('searches.summary.active')}</dt>
          <dd className="tabular">
            {page.active_count} / {page.limit}
          </dd>
        </div>
        <div className={styles.summaryRow}>
          <dt>{t('searches.summary.archived')}</dt>
          <dd className="tabular">{page.archived_count}</dd>
        </div>
        <div className={styles.summaryRow}>
          <dt>{t('searches.summary.shown')}</dt>
          <dd className="tabular">{page.total}</dd>
        </div>
      </dl>

      <ProgressBar
        percent={ratio * 100}
        label={t('searches.summary.limitLabel', { count: page.active_count, limit: page.limit })}
      />

      {/* Система сама не архивирует и не удаляет заказы (MVP_PRD §20, AC-08).
          Это обещание пользователю, а не подпись под прогресс-баром. */}
      <p className={styles.summaryNote}>{t('searches.summary.note')}</p>
    </section>
  );
}
